/**
 * One post: the answer first, three facts, the body, the questions, the sources.
 *
 * The order is the one search and AI answers read best — the lead and "W skrócie"
 * stand on their own if nothing below them is read — and it is the order of
 * every post, so a reader learns where things are once.
 */
import { useMemo } from "react";
import { useI18n, type Locale } from "@/i18n";
import { Link } from "@/components/link";
import { PageMeta } from "@/components/page-meta";
import { Faq, Crumbs, crumbSchema, faqSchema } from "@/components/marketing/faq";
import { CtaPanel, MarketingPage } from "@/components/marketing/page-parts";
import { BLOG_PATH, POSTS } from "./catalogue";
import { BODIES } from "./bodies";
import { FIGURES } from "./figures";
import { Rich } from "./rich";
import type { Block } from "./types";

export const BLOG_WORDS = {
  pl: { blog: "Poradnik", summary: "W skrócie", contents: "Spis treści", faq: "Częste pytania", sources: "Źródła", updated: "Aktualizacja", published: "Opublikowano", answer: "Odpowiedź" },
  uk: { blog: "Порадник", summary: "Коротко", contents: "Зміст", faq: "Часті питання", sources: "Джерела", updated: "Оновлено", published: "Опубліковано", answer: "Відповідь" },
  ru: { blog: "Блог", summary: "Коротко", contents: "Содержание", faq: "Частые вопросы", sources: "Источники", updated: "Обновлено", published: "Опубликовано", answer: "Ответ" },
  en: { blog: "Guides", summary: "In short", contents: "Contents", faq: "Common questions", sources: "Sources", updated: "Updated", published: "Published", answer: "Answer" },
} as const satisfies Record<Locale, Record<string, string>>;

export const formatDate = (iso: string, locale: Locale) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString(locale === "uk" ? "uk-UA" : locale === "ru" ? "ru-RU" : locale === "en" ? "en-GB" : "pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

function BlockView({ block, locale }: { block: Block; locale: Locale }) {
  switch (block.kind) {
    case "h2":
      return <h2 id={block.id} className="mt-6 scroll-mt-20 text-balance text-2xl font-bold tracking-tight">{block.text}</h2>;
    case "h3":
      return <h3 className="mt-2 text-lg font-bold tracking-tight">{block.text}</h3>;
    case "p":
      return <p className="leading-relaxed text-muted-foreground"><Rich text={block.text} /></p>;
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag className={`space-y-2 pl-5 leading-relaxed text-muted-foreground ${block.ordered ? "list-decimal" : "list-disc"}`}>
          {block.items.map((item) => <li key={item}><Rich text={item} /></li>)}
        </Tag>
      );
    }
    case "figure": {
      const Figure = FIGURES[block.figure];
      return (
        <figure className="my-2">
          <Figure locale={locale} />
          <figcaption className="mt-2 text-sm text-muted-foreground">{block.caption}</figcaption>
        </figure>
      );
    }
    case "note":
      return <p className="rounded-xl border-l-4 border-brand bg-muted/50 px-4 py-3 text-sm leading-relaxed"><Rich text={block.text} /></p>;
    case "table":
      return (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50">
              <tr>{block.head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y">
              {block.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j} className="px-3 py-2 align-top text-muted-foreground"><Rich text={cell} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "exercise":
      return (
        <ol className="list-decimal space-y-3 pl-6 leading-relaxed">
          {block.items.map((item) => (
            <li key={item.q} className="pl-1">
              <Rich text={item.q} />
              <details className="group mt-1 text-sm">
                <summary className="w-fit cursor-pointer select-none text-primary hover:underline">{BLOG_WORDS[locale].answer}</summary>
                <p className="mt-1 text-muted-foreground"><Rich text={item.a} /></p>
              </details>
            </li>
          ))}
        </ol>
      );
  }
}

export function BlogPostPage({ slug }: { slug: string }) {
  const { locale } = useI18n();
  const meta = POSTS.find((p) => p.slug === slug);
  const post = BODIES[slug]?.[locale];
  const w = BLOG_WORDS[locale];
  const schema = useMemo(
    () =>
      post && meta
        ? [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: post.title,
              description: post.description,
              datePublished: meta.published,
              dateModified: meta.updated,
              inLanguage: locale,
              author: { "@type": "Organization", name: "polskiegzamin" },
              publisher: { "@type": "Organization", name: "polskiegzamin" },
            },
            faqSchema(post.faq),
            crumbSchema({ name: w.blog, url: BLOG_PATH }, post.title),
          ]
        : [],
    [post, meta, locale, w.blog],
  );
  if (!meta || !post) return null;
  const headings = post.blocks.filter((b): b is Extract<Block, { kind: "h2" }> => b.kind === "h2");

  return (
    <MarketingPage>
      <PageMeta title={post.title} description={post.description} schema={schema} />
      <article className="flex flex-col gap-10">
        <header className="pt-4">
          <Crumbs to={BLOG_PATH} label={w.blog} />
          <h1 className="text-balance text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">{post.title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {w.published} {formatDate(meta.published, locale)}
            {meta.updated !== meta.published && ` · ${w.updated} ${formatDate(meta.updated, locale)}`}
          </p>
          <p className="mt-5 max-w-[60ch] text-pretty text-lg leading-relaxed text-muted-foreground"><Rich text={post.lead} /></p>
        </header>

        <section className="rounded-2xl border bg-card p-5 sm:p-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{w.summary}</p>
          <ul className="flex flex-col gap-2.5">
            {post.summary.map((s) => (
              <li key={s} className="flex gap-2.5 leading-relaxed">
                <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                <span><Rich text={s} /></span>
              </li>
            ))}
          </ul>
        </section>

        {headings.length > 2 && (
          <nav aria-label={w.contents} className="text-sm">
            <p className="mb-2 font-semibold">{w.contents}</p>
            <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
              {headings.map((h) => <li key={h.id}><a href={`#${h.id}`} className="hover:text-foreground hover:underline">{h.text}</a></li>)}
            </ol>
          </nav>
        )}

        <div className="flex max-w-[70ch] flex-col gap-4">
          {post.blocks.map((block, i) => <BlockView key={i} block={block} locale={locale} />)}
        </div>
      </article>

      <Faq title={w.faq} items={post.faq} />

      <section className="text-sm">
        <h2 className="mb-2 font-semibold">{w.sources}</h2>
        <ul className="space-y-1 text-muted-foreground">
          {post.sources.map((s) => (
            <li key={s.url}><a href={s.url} target="_blank" rel="noopener" className="underline-offset-4 hover:underline">{s.label}</a></li>
          ))}
        </ul>
      </section>

      <CtaPanel title={post.cta.title} text={post.cta.text} />
      <Link to={BLOG_PATH} className="-mt-8 text-sm text-primary hover:underline">← {w.blog}</Link>
    </MarketingPage>
  );
}
